import { Trans } from "@lingui/macro";
import { useCallback, useMemo, useState } from "react";
import { DropdownOutlined } from "src/atoms/DropDown";
import { FilterCountBadge } from "src/atoms/FilterCountBadge";
import {
  FilterContentWrapper,
  ScrollWrapper,
} from "src/organisms/ProductList/components/Filters/DesktopFilter.styled";
import { Box, Flex } from "@otrium/core";
import { Checkbox } from "src/atoms/Checkbox";
import { InputTextSearch } from "../InputTextSearch";
import { debounce } from "src/utils";
import { FavoriteBrandsWrapper, SearchWrapper } from "./Filter.styled";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useSegment } from "src/hooks/useSegment";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import { ResetFilters } from "src/organisms/ProductList/components/ResetFilters";
import type {
  ListingFacet,
  ListingFacetData,
} from "src/types/adapters/listing.type";
import { useLingui } from "@lingui/react";

type BrandFilterProps = {
  placeholder: string;
  facet: ListingFacet;
} & (
  | { showSearch: true; searchPlaceholder: string }
  | { showSearch?: false; searchPlaceholder?: undefined }
);

function compareByCount(a: ListingFacetData, b: ListingFacetData) {
  return b.count - a.count;
}

function BrandFilter({ placeholder, facet }: BrandFilterProps) {
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();
  const [isOpen, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { segmentProductListFilterOpened } = useSegment();
  const { refine, clearKey } = useFacetContext();
  const { _ } = useLingui();

  const items = useMemo(() => {
    if (facet.type !== "brand") {
      return [];
    }

    const filtered = searchQuery
      ? facet.data.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : facet.data;

    return [...filtered].sort(compareByCount);
  }, [facet, searchQuery]);

  const { activeCount, canRefine } = useMemo(() => {
    const activeCount =
      facet.type === "range"
        ? 0
        : facet.data.filter((option) => option.selected).length;

    const canRefine = facet.type !== "range" && facet.data.length > 0;

    return { activeCount, canRefine };
  }, [facet.type, facet.data]);

  const { data: favBrandsData } = useWishListBrands(
    9999,
    0,
    !isSessionLoading && !isLoggedIn
  );

  const favoriteBrands = useMemo(() => {
    const favoriteBrandIds =
      favBrandsData?.wishlistBrands?.items.map((item) => item?.id) || [];

    return items
      .filter((item) => {
        return favoriteBrandIds?.includes(item.id);
      })
      .map((item) => item.value);
  }, [favBrandsData, items]);

  const favoriteBrandsSelected = useMemo(() => {
    if (favoriteBrands.length === 0) {
      return false;
    }

    const lookup = new Set(favoriteBrands);
    const favItems = items.filter((item) => lookup.has(item.value));

    return favItems.length > 0 && favItems.every((item) => item.selected);
  }, [favoriteBrands, items]);

  const toggleOpen = debounce(() => {
    setOpen(!isOpen);

    if (!isOpen) {
      setSearchQuery("");
      void segmentProductListFilterOpened(facet.key);
    }
  }, 100);

  const closeDropdown = useCallback(() => {
    setOpen(false);
  }, []);

  const handleRefine = useCallback(
    (value: string) => {
      refine(facet.key, value);
    },
    [facet.key, refine]
  );

  const toggleFavoriteBrands = useCallback(() => {
    favoriteBrands.forEach((value) => {
      if (!value) {
        return;
      }

      refine(facet.key, value);
    });
  }, [facet.key, favoriteBrands, refine]);

  const handleResetFilters = useCallback(() => {
    clearKey(facet.key);
    setOpen(false);
  }, [facet.key, clearKey]);

  if (!canRefine) {
    return null;
  }

  return (
    <DropdownOutlined
      opened={isOpen}
      disabled={!canRefine}
      hasActiveFilters={activeCount > 0}
      onClose={closeDropdown}
    >
      <DropdownOutlined.Button onClick={toggleOpen}>
        <span>
          {placeholder}
          <FilterCountBadge count={activeCount} />
        </span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex flexDirection="column" maxHeight="inherit">
            <SearchWrapper>
              <Box>
                <InputTextSearch
                  placeholder={_({ id: "Search brand" })}
                  handleChange={(event) =>
                    setSearchQuery(event.currentTarget.value)
                  }
                  onClearSearch={() => setSearchQuery("")}
                />
              </Box>

              {favoriteBrands?.length ? (
                <FavoriteBrandsWrapper>
                  <Checkbox
                    name="Select my favourite brands"
                    checked={favoriteBrandsSelected}
                    onChange={toggleFavoriteBrands}
                  >
                    <span>
                      <Trans>Select my favourite brands</Trans>
                    </span>
                  </Checkbox>
                </FavoriteBrandsWrapper>
              ) : null}
            </SearchWrapper>

            <ScrollWrapper>
              <FilterContentWrapper
                as="ul"
                count={Math.round(items.length / 2)}
              >
                {items.map((item) => {
                  return (
                    <Box as="li" key={`${facet.key}-${item.value}`}>
                      <Checkbox
                        name={item.label}
                        checked={item.selected}
                        onChange={() => handleRefine(item.value)}
                      >
                        {item.label}
                      </Checkbox>
                    </Box>
                  );
                })}
              </FilterContentWrapper>
            </ScrollWrapper>

            <ResetFilters
              activeCount={activeCount}
              onClear={handleResetFilters}
            />
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { BrandFilter };
